import { useEffect, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { ApiError } from '../../../auth';
import { apiFailureReason, emberApi, isAuthRequired, type FriendCheckinPage as CheckinData } from '../../api';
import { useChatsLive, useChatsSnapshot } from '../../chats-store';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { navigate } from '../../router';
import { keepCheckinAnswer, keptCheckinAnswer } from './checkin-answer';
import { Face, FLAME, FriendsBar } from './friend-parts';

type Feel = 'keep' | 'okay' | 'stop';

/** The three answers (mock CheckIn): the word, the face's colour, its mouth. */
const FEELS: ReadonlyArray<readonly [Feel, string, string, string]> = [
  ['keep', 'Love it!', '#FBE1CF', 'M9 17c3.4 3.6 8.6 3.6 12 0'],
  ['okay', 'It’s okay', '#FFF1C7', 'M10 18c3 1.4 7 1.4 10 0'],
  ['stop', 'I’d rather stop', '#E2EEF6', 'M10 19h10'],
];

/**
 * A week in, privately — `/friends/checkin/:roomId` (2026-10-26 founder; mock CheckIn). Ember asks one of the two,
 * alone, how it is going with the other: love it, it's okay (nothing changes for either), or
 * I'd rather stop (the chat ends for both, kindly; the other reads only that it wrapped up). Answering again changes
 * it, until a stop — which cannot be taken back, so it is asked once more first, as stopping a request is
 * (2026-10-26 walk-through).
 */
export function FriendCheckinPage({ roomId }: { roomId: string }): ReactElement {
  const [page, setPage] = useState<CheckinData | null>(null);
  const [failed, setFailed] = useState<false | 'not_found' | 'load_failed'>(false);
  const [answer, setAnswer] = useState<Feel | null>(null);
  const [busy, setBusy] = useState(false);
  const [stopping, setStopping] = useState(false);

  // Read as it opens, again on every read of their Chats and when the tab comes back (2026-10-31 founder: a page follows
  // the one change signal): the other ending the chat while this is open shows here. The newest read is the page.
  useChatsLive(true);
  const chats = useChatsSnapshot().data;
  useEffect(() => {
    let alive = true;
    const load = (): void => {
      emberApi.friendCheckin(roomId)
        .then((p) => {
          if (!alive) return;
          setPage(p);
          // The face picked before, as this browser remembers it (the server keeps no answer to read back).
          if (p.status === 'open') setAnswer(keptCheckinAnswer(roomId));
        })
        .catch((err: unknown) => {
          if (!alive) return;
          if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(`/friends/checkin/${roomId}`)}`, { replace: true }); return; }
          const gone = err instanceof ApiError && err.status === 404;
          if (!gone) trackFailed('friend_checkin_load', apiFailureReason(err));
          setFailed(gone ? 'not_found' : 'load_failed');
        });
    };
    load();
    const again = (): void => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', again);
    return () => { alive = false; document.removeEventListener('visibilitychange', again); };
  }, [roomId, chats]);

  const choose = async (feel: Feel): Promise<void> => {
    if (busy || answer === 'stop' || answer === feel) return;
    if (feel === 'stop' && !stopping) { setStopping(true); return; }
    setStopping(false);
    setBusy(true);
    try {
      await emberApi.answerFriendCheckin(roomId, feel);
      keepCheckinAnswer(roomId, feel);
      setAnswer(feel);
    } catch (err: unknown) {
      trackFailed('friend_checkin_answer', apiFailureReason(err));
      toast.error('Couldn’t send that. Try again?');
    } finally {
      setBusy(false);
    }
  };

  if (failed === 'not_found') return <PageFailure title="This isn’t here." detail="It may be for someone else, or the link is old." logState="not_found" />;
  // A read again that fails leaves the page as it was.
  if (failed && !page) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!page) return <PageLoading />;

  const head = (
    <span className="nf-ember-head">
      <span className="nf-ember-head-disc">{FLAME}</span>
      <span>Ember</span>
    </span>
  );

  // A chat already over (one of the two left, or stopped before): nothing to ask about.
  if (page.status !== 'open' && answer !== 'stop') {
    return (
      <main className="nf-page" data-log-seen="friend_checkin" data-log-state="closed" data-log-match-id={roomId}>
        <FriendsBar>{head}</FriendsBar>
        <div className="nf-center-body">
          <div className="nf-center-text">
            <h1 className="nf-title is-step">This chat has wrapped up</h1>
            {/* A kind word and a way on (2026-10-27 walk-through: the bare line, the other's last question unseen above it, and
                nowhere to go) — never who ended it, nor why. */}
            <p>That happens, and it’s okay. What you two said stays here for you to read.</p>
          </div>
        </div>
        <div className="nf-foot">
          <Link to="/friends/new" className="nf-cta is-block" data-log="friend_checkin_done_again">Meet someone else</Link>
          <Link to={`/talk?mode=chat&room=${encodeURIComponent(roomId)}`} className="nf-text-btn" data-log="friend_checkin_done_read">Read your chat</Link>
        </div>
      </main>
    );
  }

  const name = page.them.name;
  return (
    <main className="nf-page" data-log-seen="friend_checkin" data-log-state="open" data-log-match-id={roomId}>
      <FriendsBar>{head}</FriendsBar>
      <div className="nf-chat">
        <div className="nf-day">One week with {name}</div>
        <div className="nf-bubble">Just between us: how’s it going with {name}?</div>

        <div className="nf-feel" role="group" aria-label={`How’s it going with ${name}?`}>
          {FEELS.map(([feel, label, tint, mouth]) => (
            <button
              type="button"
              key={feel}
              className={`nf-feel-btn${answer === feel ? ' is-on' : ''}`}
              aria-pressed={answer === feel}
              disabled={busy || answer === 'stop'}
              data-log={feel === 'stop' ? 'friend_checkin_stop_open' : 'friend_checkin_answer'}
              data-log-state={feel}
              data-log-match-id={roomId}
              onClick={() => { void choose(feel); }}
            >
              <span className="nf-feel-face" style={{ background: tint }}>
                <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden>
                  <circle cx="11" cy="12" r="1.8" fill="#2D251F" /><circle cx="19" cy="12" r="1.8" fill="#2D251F" />
                  <path d={mouth} stroke="#2D251F" strokeWidth="2" fill="none" strokeLinecap="round" />
                </svg>
              </span>
              <span>{label}</span>
            </button>
          ))}
        </div>

        {answer === 'keep' && (
          <div className="nf-after">
            <div className="nf-bubble">Yay! So glad you two clicked.</div>
            <section className="nf-week">
              <span className="nf-week-faces">
                <Face avatar={page.me.avatar} size={52} className="is-ring-navy" />
                <Face avatar={page.them.avatar} size={52} className="is-ring-navy" />
              </span>
              <span className="nf-week-text">
                <span className="nf-hand is-gold">one week of you two!</span>
                {page.lines > 0 && <span className="nf-week-sub">{plural(page.lines, 'message')}</span>}
              </span>
            </section>
          </div>
        )}
        {answer === 'okay' && (
          <div className="nf-after">
            <div className="nf-bubble">Got it. Thanks for telling me.</div>
          </div>
        )}
        {answer === 'stop' && (
          <div className="nf-after">
            <div className="nf-bubble">Thanks for telling me. It’s wrapped up now, kindly, no fuss.</div>
            <div className="nf-after-btns">
              {/* It opens a new request now, so it says so — the closed page's words (it read "Try someone else later"). */}
              <button type="button" className="is-dark" data-log="friend_checkin_next" data-log-state="try_again" onClick={() => navigate('/friends/new')}>Meet someone else</button>
              <button type="button" className="is-light" data-log="friend_checkin_next" data-log-state="no_thanks" onClick={() => navigate('/')}>No thanks</button>
            </div>
          </div>
        )}
      </div>

      <div className="nf-composer">
        <Link to="/talk?mode=chat" data-log="friend_checkin_message">Message Ember</Link>
      </div>

      {stopping && (
        <BottomSheet
          title={`End your chat with ${name}?`}
          description={`It closes for both of you. ${name} only sees that it wrapped up.`}
          logSeen="friend_checkin_stop_sheet"
          onClose={() => setStopping(false)}
        >
          <button type="button" className="ember-btn nf-btn-dark" disabled={busy} data-log="friend_checkin_answer" data-log-state="stop" data-log-match-id={roomId} onClick={() => { void choose('stop'); }}>
            End the chat
          </button>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="friend_checkin_stop_cancel" data-log-match-id={roomId} onClick={() => setStopping(false)}>
            Keep chatting
          </button>
        </BottomSheet>
      )}
    </main>
  );
}
